/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Link,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  useMediaQuery
} from "@mui/material"
import { collection, doc, addDoc, DocumentReference, onSnapshot, deleteDoc } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db, functions } from "../../firebase"
import { httpsCallable } from "firebase/functions"
import { tokens } from "../../theme"
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import dayjs from "dayjs"
import * as EmailValidator from 'email-validator'
import { CustomerStatusIcon } from "./lib/CustomerStatusIcon"
import { ICustomer } from "../../models/customer"

const sendMail = httpsCallable(functions, 'sendMail')
const mailTemplate = (id: string) => {
return `
取引先登録の準備を開始しました。
以下リンクより取引先情報の入力をお願いします。

https://traceability.rindou.info/customers/${id}/regist
`
}

const Customers = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [customerEmail, setCustomerEmail] = useState<string|null>('')
  const [items, setItems] = useState<Array<object>>([])
  const [emailError, setEmailError] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>("")
  const customersRef = collection(db, 'customers')

  // 新規取引先追加用の状態
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [newCustomer, setNewCustomer] = useState<ICustomer>({
    email: '',
    companyName: '',
    companyAddress: '',
    companyTel: '',
    companyManager: '',
    notifications: '',
    status: 'completed',
    updatedAt: null
  })
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({})

  const columns = isMobile ? [
    {
      field: "No",
      headerName: "No",
      width: 50,
      sortComparator: gridNumberComparator,
    },
    {
      field: "companyName",
      headerName: "顧客名",
      cellClassName: "name-column--cell",
      width: 150,
    },
    {
      field: "status",
      headerName: "状態",
      width: 80,
      renderCell: (params: any) => (
        <CustomerStatusIcon customerId={params.row.id} status={params.row.status} />
      ),
    },
    {
      field: "cancel",
      headerName: "削除",
      width: 70,
      renderCell: (params: any) => (
        params.row.status === "init" && < Link color="secondary" style={{ cursor: "pointer" }} onClick = {() => cancel(params.row.id)}> 削除</Link >
      ),
    }
  ] : [
    {
      field: "No",
      headerName: "Mo",
      width: 10,
      sortComparator: gridNumberComparator,
    },
    {
      field: "receptionDatetime",
      headerName: "受付日時",
      flex: 1,
    },
    {
      field: "email",
      headerName: "メールアドレス",
      flex: 1,
    },
    {
      field: "companyName",
      headerName: "顧客名",
      cellClassName: "name-column--cell",
      flex: 1,
    },
    {
      field: "status",
      headerName: "状態",
      flex: 1,
      renderCell: (params: any) => (
        <CustomerStatusIcon customerId={params.row.id} status={params.row.status} />
      ),
    },
    {
      field: "cancel",
      headerName: "キャンセル",
      flex: 1,
      renderCell: (params: any) => (
        params.row.status === "init" && < Link color="secondary" style={{ cursor: "pointer" }} onClick = {() => cancel(params.row.id)}> キャンセル</Link >
      ),
    }
  ]

  useEffect(() => {
    const unsubscribe = onSnapshot(customersRef, ({ docs }) => {
      setItems(docs.map((_, i) => {
        return ({ No: i+1, ..._.data(), id: _.id })
      }))
    })
    return unsubscribe
  }, [])

  const onChange = (e: any) => {
    setCustomerEmail(e.target.value)
  }

  const add = async () => {
    if (!customerEmail) return
    if (!EmailValidator.validate(customerEmail)) {
      setEmailError(true)
      setErrorMsg("メールアドレスが不正です")
      return
    }
    setCustomerEmail("")
    setEmailError(false)
    addDoc(collection(db, "customers"), { email: customerEmail, receptionDatetime: dayjs().format("YYYY/MM/DD HH:mm"), status: "init" }).then(async (docRef: DocumentReference) => {
      await sendMail({
        to: customerEmail,
        subject: "[りんどう] 取引先情報の入力をお願いします",
        type: "text",
        contents: mailTemplate(docRef.id)
      })
      toast.success("取引先情報の登録を開始しました")
    }).catch(e => {
      toast.error(JSON.stringify(e))
    })
  }

  const cancel = async (id: string) => {
    if(!window.confirm("取引先情報を削除しますか？")) return
    await deleteDoc(doc(collection(db, "customers"), id))
      .then(() => {
        toast.success("取引先情報を削除しました")
      })
      .catch(e => {
        toast.error(JSON.stringify(e))
      })
  }

  // 新規取引先追加のためのフォーム処理
  const handleNewCustomerChange = (field: keyof ICustomer, value: string) => {
    setNewCustomer(prev => ({ ...prev, [field]: value }))
    // エラーをクリア
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!newCustomer.email) {
      errors.email = 'メールアドレスは必須です'
    } else if (!EmailValidator.validate(newCustomer.email)) {
      errors.email = 'メールアドレスが不正です'
    }

    if (!newCustomer.companyName) {
      errors.companyName = '会社名は必須です'
    }

    if (!newCustomer.companyAddress) {
      errors.companyAddress = '住所は必須です'
    }

    if (!newCustomer.companyTel) {
      errors.companyTel = '電話番号は必須です'
    }

    if (!newCustomer.companyManager) {
      errors.companyManager = '担当者名は必須です'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAddNewCustomer = async () => {
    if (!validateForm()) return

    try {
      const customerData = {
        ...newCustomer,
        receptionDatetime: dayjs().format("YYYY/MM/DD HH:mm"),
        updatedAt: new Date()
      }

      await addDoc(collection(db, "customers"), customerData)
      toast.success("新規取引先を追加しました")

      // フォームをリセット
      setNewCustomer({
        email: '',
        companyName: '',
        companyAddress: '',
        companyTel: '',
        companyManager: '',
        notifications: '',
        status: 'completed',
        updatedAt: null
      })
      setFormErrors({})
      setOpenDialog(false)
    } catch (e) {
      toast.error("取引先の追加に失敗しました: " + JSON.stringify(e))
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setFormErrors({})
    setNewCustomer({
      email: '',
      companyName: '',
      companyAddress: '',
      companyTel: '',
      companyManager: '',
      notifications: '',
      status: 'completed',
      updatedAt: null
    })
  }

  return (
    <Box m={isMobile ? 2 : 5}>
      <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="start" alignContent="center" gap={isMobile ? 1 : 2}>
        <TextField error={emailError} value={customerEmail} helperText={errorMsg} style={{ width: isMobile ? '100%' : 300 }} label="取引先Eメールアドレスを入力" onChange={onChange}/>
        <Button variant="contained" color="info" onClick={add} fullWidth={isMobile}>メール送信で追加</Button>
        <Button variant="contained" color="success" onClick={() => setOpenDialog(true)} fullWidth={isMobile}>新規取引先を直接追加</Button>
      </Box>
      <Box
        m={isMobile ? 1 : 2}
        height={isMobile ? "70vh" : "80vh"}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid rows={items} columns={columns} />
      </Box>

      {/* 新規取引先追加モーダル */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>新規取引先追加</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="メールアドレス *"
                value={newCustomer.email}
                onChange={(e) => handleNewCustomerChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="会社名 *"
                value={newCustomer.companyName}
                onChange={(e) => handleNewCustomerChange('companyName', e.target.value)}
                error={!!formErrors.companyName}
                helperText={formErrors.companyName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="住所 *"
                value={newCustomer.companyAddress}
                onChange={(e) => handleNewCustomerChange('companyAddress', e.target.value)}
                error={!!formErrors.companyAddress}
                helperText={formErrors.companyAddress}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="電話番号 *"
                value={newCustomer.companyTel}
                onChange={(e) => handleNewCustomerChange('companyTel', e.target.value)}
                error={!!formErrors.companyTel}
                helperText={formErrors.companyTel}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="担当者名 *"
                value={newCustomer.companyManager}
                onChange={(e) => handleNewCustomerChange('companyManager', e.target.value)}
                error={!!formErrors.companyManager}
                helperText={formErrors.companyManager}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="通知設定"
                value={newCustomer.notifications}
                onChange={(e) => handleNewCustomerChange('notifications', e.target.value)}
                multiline
                rows={3}
                placeholder="通知に関する設定や備考を入力してください"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            キャンセル
          </Button>
          <Button onClick={handleAddNewCustomer} variant="contained" color="primary">
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Customers
